import {
  alpha,
  FormHelperText,
  Stack,
  TextFieldProps,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Controller, useFormContext } from 'react-hook-form';

import Pen from '@/shared/ui/icons/Pen.svg';

import { BlurGuardTextfield } from '../textfield/BlurGuardTextfield';

interface IProps {
  fieldName: string;
  maxLength?: number;
  isRenderHelperText?: boolean;
  validate?: (value?: string) => boolean | string;
  isShowIcon?: boolean;
  digitsOnly?: boolean;
}

/** Атрибуты поля «только цифры»: цифровая клавиатура на телефоне (iOS — по `pattern`) */
const DIGITS_INPUT_PROPS = { inputMode: 'numeric', pattern: '[0-9]*' } as const;

/**
 * Оставляет в строке только цифры и режет до лимита: вставка «12ab345» в поле
 * на 4 знака даёт «1234», а не «12345» поверх `maxLength`
 * @param value - введённый текст
 * @param maxLength - лимит длины
 */
const keepDigits = (value: string, maxLength?: number) =>
  value.replace(/\D/g, '').slice(0, maxLength);

/**
 * Компонент текстового поля для формы.
 * @param props - Свойства компонента.
 * @param props.fieldName - название поля для формы
 * @param props.maxLength - максимальная длина символов
 * @param props.isRenderHelperText - пропс для отображения хелпера текста
 * @param props.required - обязательно ли поле
 * @param props.validate - кастомная проверка валидации
 * @param props.isShowIcon - пропс для отображения иконки
 * @param props.digitsOnly - только цифры: нецифровые символы отбрасываются при вводе,
 * клавиатура цифровая. Не `type='number'`: он игнорирует `maxLength`, принимает «e»/«-»
 * и крутится колесом
 * @param props.disabled - пропс для отключения поля
 * @returns - Рендер компонента.
 */
export const FormTextField = ({
  fieldName,
  maxLength,
  isRenderHelperText = true,
  required,
  validate,
  isShowIcon = true,
  digitsOnly = false,
  disabled,
  ...otherProps
}: IProps & TextFieldProps) => {
  const { control } = useFormContext();
  const { palette } = useTheme();
  const htmlInputProps = digitsOnly
    ? { maxLength, ...DIGITS_INPUT_PROPS }
    : { maxLength };

  /**
   * Значение для формы: у поля «только цифры» — без нецифровых символов
   * @param value - введённый текст
   */
  const toFieldValue = (value: string) =>
    digitsOnly ? keepDigits(value, maxLength) : value;

  /**
   * Рендер хелпера текста
   * @param length - длина текста
   * @param isOverLimit - превышена ли максимальная длина
   */
  const renderHelperText = (length: number, isOverLimit: boolean) => {
    if (maxLength) {
      return (
        <Typography
          sx={{
            color: disabled
              ? alpha(palette.text.secondary, 0.38)
              : isOverLimit
                ? palette.error.main
                : length > 0
                  ? palette.success.main
                  : palette.text.secondary
          }}
          variant='body/roboto/12'
        >
          {length} / {maxLength}
        </Typography>
      );
    }
    return undefined;
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{
        required: required ? 'Поле обязательно для заполнения' : undefined,
        validate
      }}
      render={({ field, fieldState }) => {
        // Нормализуем символы перевода строки для корректного подсчета
        const safeValue = field.value ?? '';
        const normalizedValue = safeValue.replace(/\r\n/g, '\n');
        const length = normalizedValue.length;
        const isOverLimit = maxLength ? length > maxLength : false;
        const error = fieldState.error;
        const errorMessage = fieldState.error?.message;

        return (
          <Stack>
            <BlurGuardTextfield
              {...field}
              value={safeValue}
              onChange={event =>
                field.onChange(toFieldValue(event.target.value))
              }
              onBlur={event => {
                // Триммим значение при потере фокуса
                const trimmedValue = event.target.value.trim();
                field.onChange(trimmedValue);
                field.onBlur();
              }}
              slotProps={{
                htmlInput: htmlInputProps,
                input: {
                  endAdornment: isShowIcon ? (
                    <Pen
                      color={
                        disabled ? palette.grey[400] : palette.action.active
                      }
                    />
                  ) : (
                    ''
                  )
                },
                inputLabel: {
                  shrink: true
                }
              }}
              error={!!error || isOverLimit}
              helperText={
                isRenderHelperText ? renderHelperText(length, isOverLimit) : ''
              }
              required={required}
              disabled={disabled}
              {...otherProps}
            />
            {errorMessage && (
              <FormHelperText sx={{ color: palette.error.main }}>
                {errorMessage}
              </FormHelperText>
            )}
          </Stack>
        );
      }}
    />
  );
};
